import React, { useState, useEffect, useMemo } from 'react';

// --- Helper & UI Components ---

const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-20">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-vml-blue"></div>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`glass-card p-6 mb-6 ${className}`}>
    {children}
  </div>
);

// --- NEW: Analysis Report Components ---

const ReportContent = ({ htmlContent }) => {
  // This component safely renders the HTML from Airtable and applies dashboard-specific styling.
  return (
    <div className="report-prose pt-4">
      <style jsx global>{`
        .report-prose h2 {
          font-size: 1.25rem; /* 20px */
          font-weight: 700;
          color: #334155;
          border-bottom: 2px solid #0099FF;
          padding-bottom: 8px;
          margin-top: 1.5rem;
          margin-bottom: 1rem;
        }
        .report-prose h2:first-child {
          margin-top: 0;
        }
        .report-prose p, .report-prose li {
          font-size: 0.9375rem; /* 15px */
          color: #475569;
          line-height: 1.6;
        }
        .report-prose ul {
          padding-left: 20px;
          list-style-type: disc;
        }
        .report-prose li {
          margin-bottom: 0.5rem;
        }
        .report-prose strong {
          color: #334155;
        }
      `}</style>
      <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
    </div>
  );
};


const AnalysisReport = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        // Fetches data from the 'analysis' table
        const res = await fetch(`/api/airtable?table=analysis`);
        if (!res.ok) {
          throw new Error('Failed to fetch analysis report.');
        }
        const data = await res.json();
        // Assuming the latest report is the first record returned
        if (data.records && data.records.length > 0) {
          setReport(data.records[0].fields);
        }
      } catch (err) {
        setError(err.message);
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, []);

  const ChevronIcon = ({ isOpen }) => (
    <svg className={`w-5 h-5 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
    </svg>
  );

  return (
    <div className="mb-6 overflow-hidden rounded-2xl">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-6 text-left text-white bg-vml-gradient rounded-2xl focus:outline-none hover:shadow-vml-lg hover:scale-[1.01] transition-all duration-300"
      >
        <div>
          <span className="font-bold text-lg block">Latest Video Activity Analysis</span>
          <span className="text-sm text-white/80 font-normal">{isOpen ? 'Click to collapse' : 'Click to expand'}</span>
        </div>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {isOpen && (
        <div className="px-6 pb-6 bg-white/70 backdrop-blur-lg">
          {loading && <div className="p-4 text-center">Loading Report...</div>}
          {error && <div className="p-4 text-center text-red-500">Error: {error}</div>}
          {report ? (
            <div>
              <p className="text-sm text-gray-500 mb-2">Report Date: {new Date(report.Report_Date).toLocaleDateString()}</p>
              <ReportContent htmlContent={report.Content} />
            </div>
          ) : (
            !loading && <div className="p-4 text-center text-gray-500">No report found.</div>
          )}
        </div>
      )}
    </div>
  );
};


// --- Main Application Components (Existing Code) ---

const Header = ({ logoUrl }) => (
  <header className="bg-white shadow-vml-sm p-6 sticky top-0 z-20">
    <div className="container mx-auto flex justify-between items-center">
      <div className="flex items-center space-x-4">
        <img src={logoUrl} alt="VML Logo" className="h-10 w-auto" />
        <div className="h-8 w-px bg-gray-300"></div>
        <h1 className="text-2xl font-bold text-gray-900">RCL Foods - YouTube competitor analysis</h1>
      </div>
    </div>
  </header>
);

const FilterControls = ({ brands, categories, onBrandChange, onCategoryChange }) => (
  <div className="glass-card p-6 mb-6 sticky top-[88px] z-10">
    <div className="flex flex-wrap items-center gap-4">
      <div className="flex-grow">
        <label htmlFor="category-select" className="field-label">Category</label>
        <select
          id="category-select"
          onChange={(e) => onCategoryChange(e.target.value)}
          className="input-field"
        >
          <option value="">All Categories</option>
          {categories.map(category => <option key={category} value={category}>{category}</option>)}
        </select>
      </div>
      <div className="flex-grow">
        <label htmlFor="brand-select" className="field-label">Brand</label>
        <select
          id="brand-select"
          onChange={(e) => onBrandChange(e.target.value)}
          className="input-field"
        >
          <option value="">All Brands</option>
          {brands.map(brand => <option key={brand} value={brand}>{brand}</option>)}
        </select>
      </div>
    </div>
  </div>
);

const YouTubePlayer = ({ videoUrl }) => {
  const [isLoaded, setIsLoaded] = useState(false);

  // Extract YouTube video ID from various URL formats
  const getVideoId = (url) => {
    if (!url || typeof url !== 'string') return null;

    // Handle different YouTube URL formats
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return match[1];
      }
    }

    console.error("Could not extract YouTube video ID from URL:", url);
    return null;
  };

  const videoId = getVideoId(videoUrl);

  if (!videoId) {
    return (
      <div className="aspect-w-16 aspect-h-9 bg-gray-200 flex items-center justify-center rounded-lg">
        <p className="text-red-500 text-sm p-4 text-center">Invalid or unsupported YouTube URL.</p>
      </div>
    );
  }

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  return (
    <div className="aspect-w-16 aspect-h-9 relative">
      {!isLoaded && (
        <div
          className="absolute inset-0 bg-gray-900 rounded-lg flex items-center justify-center cursor-pointer group"
          style={{ backgroundImage: `url(${thumbnailUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          onClick={() => setIsLoaded(true)}
        >
          <div className="absolute inset-0 bg-black/30 rounded-lg group-hover:bg-black/40 transition-all duration-300"></div>
          <div className="relative z-10 w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:bg-red-700 group-hover:scale-110 transition-all duration-300">
            <svg className="w-8 h-8 text-white ml-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z"/>
            </svg>
          </div>
        </div>
      )}
      {isLoaded && (
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="w-full h-full rounded-lg shadow-lg"
        ></iframe>
      )}
    </div>
  );
};

const VideoList = ({ videos }) => {
  if (videos.length === 0) {
    return (
      <Card>
        <p className="text-center text-gray-500">No videos match the current filters.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-8">
      {videos.map(video => (
        <Card key={video.id}>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-6 items-start">
            <div className="md:col-span-2">
              <YouTubePlayer videoUrl={video.Video_URL} />
            </div>
            <div className="md:col-span-3">
              <h3 className="text-xl font-bold text-gray-900">{video.Video_Title || 'No Title'}</h3>
              <p className="text-sm text-gray-500 mb-3">by {video.Channel_Name || 'Unknown Channel'}</p>
              <p className="text-base text-gray-600 mb-4">{video.Description || 'No description available.'}</p>
              <div className="flex flex-wrap gap-2 text-sm">
                <span className="bg-vml-blue/10 text-vml-blue px-3 py-1 rounded-full font-semibold">{video.Brand}</span>
                <span className="bg-vml-green/10 text-vml-green px-3 py-1 rounded-full font-semibold">{video.Category}</span>
                <span className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full">
                  Published: {video.Published ? new Date(video.Published).toLocaleDateString() : 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

const Pagination = ({ videosPerPage, totalVideos, paginate, currentPage }) => {
    const pageNumbers = [];
    for (let i = 1; i <= Math.ceil(totalVideos / videosPerPage); i++) {
        pageNumbers.push(i);
    }

    if (pageNumbers.length <= 1) return null;

    return (
        <nav className="mt-8">
            <ul className="flex justify-center space-x-2">
                {pageNumbers.map(number => (
                    <li key={number}>
                        <button
                            onClick={() => paginate(number)}
                            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${currentPage === number ? 'bg-vml-gradient text-white shadow-vml-md' : 'bg-white/70 text-gray-700 hover:bg-white hover:shadow-vml-sm'}`}
                        >
                            {number}
                        </button>
                    </li>
                ))}
            </ul>
        </nav>
    );
};


// --- Main App Component ---

function App() {
  // State
  const [allAds, setAllAds] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const VML_LOGO_URL = "/logos/BLACK Wordmark VML.png";
  const VIDEOS_PER_PAGE = 15;

  // Data Fetching
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/airtable?table=youtube_ads`);
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || 'Failed to fetch data from Airtable.');
        }
        const data = await res.json();
        const records = (data.records || []).map(rec => ({ id: rec.id, ...rec.fields }));

        // Sort by most recent first
        records.sort((a, b) => new Date(b.Published) - new Date(a.Published));

        setAllAds(records);

        // Populate dropdowns with unique, sorted values
        const uniqueBrands = [...new Set(records.map(r => r.Brand).filter(Boolean))].sort();
        const uniqueCategories = [...new Set(records.map(r => r.Category).filter(Boolean))].sort();
        setBrands(uniqueBrands);
        setCategories(uniqueCategories);

      } catch (err)
 {
        setError(err.message);
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtering Logic
  const filteredAds = useMemo(() => {
    return allAds.filter(ad => {
      const brandMatch = !selectedBrand || ad.Brand === selectedBrand;
      const categoryMatch = !selectedCategory || ad.Category === selectedCategory;
      return brandMatch && categoryMatch;
    });
  }, [allAds, selectedBrand, selectedCategory]);

  // Pagination Logic
  const indexOfLastVideo = currentPage * VIDEOS_PER_PAGE;
  const indexOfFirstVideo = indexOfLastVideo - VIDEOS_PER_PAGE;
  const currentVideos = filteredAds.slice(indexOfFirstVideo, indexOfLastVideo);

  const handleBrandChange = (brand) => {
    setSelectedBrand(brand);
    setCurrentPage(1);
  };

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  return (
    <div className="bg-gray-50 min-h-screen font-sans">
      <Header logoUrl={VML_LOGO_URL} />
      <main className="container mx-auto p-6">
        {loading && <LoadingSpinner />}
        {error && (
          <div className="text-center text-red-600 bg-red-100 p-6 rounded-md shadow-md">
            <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
            <pre className="text-sm text-left whitespace-pre-wrap">{error}</pre>
          </div>
        )}
        {!loading && !error && (
          <>
            <div className="glass-card p-6 mb-6">
              <h2 className="text-xl font-bold text-gray-900 mb-2">Overview</h2>
              <p className="text-gray-600">
                This tool collects videos from the identified YouTube channels and is updated daily. You may use the drop down filters to select the categories and brands you are interested in. There is also a detailed AI Analysis section in which you are able to see an overall analysis of video activity from the tracked channels. To add additional competitors or categories please speak to your VML agency team.
              </p>
            </div>

            <FilterControls
              brands={brands}
              categories={categories}
              onBrandChange={handleBrandChange}
              onCategoryChange={handleCategoryChange}
            />
            
            <AnalysisReport />

            <VideoList videos={currentVideos} />
            <Pagination 
                videosPerPage={VIDEOS_PER_PAGE}
                totalVideos={filteredAds.length}
                paginate={setCurrentPage}
                currentPage={currentPage}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default App;
